import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Routes, Route, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { 
  Loader2, 
  LogOut, 
  LayoutDashboard, 
  FileText, 
  Sparkles, 
  Download,
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  Star,
  StarOff,
  RefreshCw,
  Upload,
  Clock,
  Settings,
  Play,
  FileUp
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { id: 'actualites', name: 'Actualités' },
  { id: 'histoire', name: 'Histoire' },
  { id: 'culture', name: 'Culture' },
  { id: 'politique', name: 'Politique' },
  { id: 'sport', name: 'Sport' },
];

// Dashboard Overview Component
const DashboardOverview = ({ stats }) => {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-playfair text-2xl font-bold text-stone-900">Tableau de bord</h2>
        <p className="font-inter text-stone-500 mt-1">Vue d'ensemble de votre site</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 border border-stone-200">
          <p className="font-inter text-sm text-stone-500 uppercase tracking-wider">Total articles</p>
          <p className="font-playfair text-3xl font-bold text-stone-900 mt-2">{stats.total}</p>
        </div>
        <div className="bg-white p-6 border border-stone-200">
          <p className="font-inter text-sm text-stone-500 uppercase tracking-wider">Publiés</p>
          <p className="font-playfair text-3xl font-bold text-green-700 mt-2">{stats.published}</p>
        </div>
        <div className="bg-white p-6 border border-stone-200">
          <p className="font-inter text-sm text-stone-500 uppercase tracking-wider">Actualités</p>
          <p className="font-playfair text-3xl font-bold text-red-800 mt-2">{stats.by_category?.actualites || 0}</p>
        </div>
        <div className="bg-white p-6 border border-stone-200">
          <p className="font-inter text-sm text-stone-500 uppercase tracking-wider">Histoire</p>
          <p className="font-playfair text-3xl font-bold text-amber-700 mt-2">{stats.by_category?.histoire || 0}</p>
        </div>
      </div>

      {/* Source breakdown */}
      <div className="bg-white p-6 border border-stone-200">
        <h3 className="font-playfair text-lg font-semibold mb-4">Articles par source</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-stone-50">
            <p className="font-inter text-2xl font-bold text-stone-900">{stats.by_source?.manual || 0}</p>
            <p className="font-inter text-xs text-stone-500 uppercase">Manuels</p>
          </div>
          <div className="text-center p-4 bg-blue-50">
            <p className="font-inter text-2xl font-bold text-blue-700">{stats.by_source?.scraping || 0}</p>
            <p className="font-inter text-xs text-stone-500 uppercase">Scraping</p>
          </div>
          <div className="text-center p-4 bg-purple-50">
            <p className="font-inter text-2xl font-bold text-purple-700">{stats.by_source?.ai || 0}</p>
            <p className="font-inter text-xs text-stone-500 uppercase">IA</p>
          </div>
          <div className="text-center p-4 bg-amber-50">
            <p className="font-inter text-2xl font-bold text-amber-700">{stats.by_source?.pdf || 0}</p>
            <p className="font-inter text-xs text-stone-500 uppercase">PDF</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Articles Management Component
const ArticlesManagement = ({ articles, onRefresh, getAuthHeader }) => {
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: 'actualites',
    image_url: '',
    author: 'Rédaction VersaillesPlus'
  });

  const resetForm = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      category: 'actualites',
      image_url: '',
      author: 'Rédaction VersaillesPlus'
    });
  };

  const handleCreate = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/articles`, formData, {
        headers: getAuthHeader()
      });
      toast.success('Article créé avec succès');
      setIsCreateOpen(false);
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/articles/${selectedArticle.id}`, formData, {
        headers: getAuthHeader()
      });
      toast.success('Article modifié avec succès');
      setIsEditOpen(false);
      onRefresh();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    try {
      await axios.delete(`${API_URL}/articles/${id}`, {
        headers: getAuthHeader()
      });
      toast.success('Article supprimé');
      onRefresh();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const togglePublish = async (article) => {
    try {
      await axios.put(`${API_URL}/articles/${article.id}`, {
        is_published: !article.is_published
      }, { headers: getAuthHeader() });
      toast.success(article.is_published ? 'Article dépublié' : 'Article publié');
      onRefresh();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const toggleFeatured = async (article) => {
    try {
      await axios.put(`${API_URL}/articles/${article.id}`, {
        is_featured: !article.is_featured
      }, { headers: getAuthHeader() });
      toast.success(article.is_featured ? 'Retiré de la une' : 'Ajouté à la une');
      onRefresh();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const openEditDialog = (article) => {
    setSelectedArticle(article);
    setFormData({
      title: article.title,
      excerpt: article.excerpt,
      content: article.content,
      category: article.category,
      image_url: article.image_url || '',
      author: article.author
    });
    setIsEditOpen(true);
  };

  const sourceLabel = (type) => {
    switch(type) {
      case 'ai': return <span className="text-purple-600">IA</span>;
      case 'scraping': return <span className="text-blue-600">Scraping</span>;
      case 'pdf': return <span className="text-amber-600">PDF</span>;
      default: return <span className="text-stone-600">Manuel</span>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-playfair text-2xl font-bold text-stone-900">Articles</h2>
          <p className="font-inter text-stone-500 mt-1">{articles.length} articles</p>
        </div>
        
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-red-800 hover:bg-red-900" data-testid="create-article-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nouvel article
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-playfair text-xl">Créer un article</DialogTitle>
              <DialogDescription>Créez un nouvel article pour VersaillesPlus</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Titre</Label>
                <Input 
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Titre de l'article"
                  data-testid="article-title-input"
                />
              </div>
              <div>
                <Label>Catégorie</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(v) => setFormData({...formData, category: v})}
                >
                  <SelectTrigger data-testid="article-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Extrait</Label>
                <Textarea 
                  value={formData.excerpt}
                  onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                  placeholder="Court résumé de l'article"
                  rows={2}
                  data-testid="article-excerpt-input"
                />
              </div>
              <div>
                <Label>Contenu</Label>
                <Textarea 
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  placeholder="Contenu complet de l'article"
                  rows={10}
                  data-testid="article-content-input"
                />
              </div>
              <div>
                <Label>URL de l'image</Label>
                <Input 
                  value={formData.image_url}
                  onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                  placeholder="https://..."
                  data-testid="article-image-input"
                />
              </div>
              <div>
                <Label>Auteur</Label>
                <Input 
                  value={formData.author}
                  onChange={(e) => setFormData({...formData, author: e.target.value})}
                  data-testid="article-author-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Annuler</Button>
              <Button onClick={handleCreate} disabled={loading} className="bg-red-800 hover:bg-red-900" data-testid="save-article-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-playfair text-xl">Modifier l'article</DialogTitle>
            <DialogDescription>Modifiez les informations de l'article</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Titre</Label>
              <Input 
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
              />
            </div>
            <div>
              <Label>Catégorie</Label>
              <Select 
                value={formData.category} 
                onValueChange={(v) => setFormData({...formData, category: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Extrait</Label>
              <Textarea 
                value={formData.excerpt}
                onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                rows={2}
              />
            </div>
            <div>
              <Label>Contenu</Label>
              <Textarea 
                value={formData.content}
                onChange={(e) => setFormData({...formData, content: e.target.value})}
                rows={10}
              />
            </div>
            <div>
              <Label>URL de l'image</Label>
              <Input 
                value={formData.image_url}
                onChange={(e) => setFormData({...formData, image_url: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>Annuler</Button>
            <Button onClick={handleEdit} disabled={loading} className="bg-red-800 hover:bg-red-900">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Table */}
      <div className="bg-white border border-stone-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-inter">Titre</TableHead>
              <TableHead className="font-inter">Catégorie</TableHead>
              <TableHead className="font-inter">Source</TableHead>
              <TableHead className="font-inter">Statut</TableHead>
              <TableHead className="font-inter">Date</TableHead>
              <TableHead className="font-inter text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {articles.map((article) => (
              <TableRow key={article.id}>
                <TableCell className="font-medium max-w-xs truncate" title={article.title}>
                  {article.title}
                </TableCell>
                <TableCell>
                  <span className="category-label text-red-800 text-[10px]">
                    {categories.find(c => c.id === article.category)?.name || article.category}
                  </span>
                </TableCell>
                <TableCell className="text-sm">
                  {sourceLabel(article.generation_type)}
                </TableCell>
                <TableCell>
                  <span className={`inline-flex items-center px-2 py-1 text-xs font-inter ${
                    article.is_published ? 'bg-green-100 text-green-800' : 'bg-stone-100 text-stone-600'
                  }`}>
                    {article.is_published ? 'Publié' : 'Brouillon'}
                  </span>
                </TableCell>
                <TableCell className="text-sm text-stone-500">
                  {formatDistanceToNow(new Date(article.created_at), { addSuffix: true, locale: fr })}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => toggleFeatured(article)}
                      title={article.is_featured ? 'Retirer de la une' : 'Mettre à la une'}
                    >
                      {article.is_featured ? <Star className="h-4 w-4 text-amber-500 fill-amber-500" /> : <StarOff className="h-4 w-4" />}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => togglePublish(article)}
                      title={article.is_published ? 'Dépublier' : 'Publier'}
                    >
                      {article.is_published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEditDialog(article)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(article.id)}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// Content Generation Component (Scraping + AI + PDF)
const ContentGeneration = ({ getAuthHeader, onRefresh }) => {
  const [scrapingLoading, setScrapingLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiCategory, setAiCategory] = useState('histoire');
  const [selectedSource, setSelectedSource] = useState('all');
  const [scrapingJobs, setScrapingJobs] = useState([]);
  const [aiJobs, setAiJobs] = useState([]);
  const [pdfUploads, setPdfUploads] = useState([]);
  const [scrapingSources, setScrapingSources] = useState([]);

  const fetchJobs = useCallback(async () => {
    try {
      const [scrapingRes, aiRes, pdfRes, sourcesRes] = await Promise.all([
        axios.get(`${API_URL}/scraping/jobs`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/ai/jobs`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/pdf/uploads`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/scraping/sources`, { headers: getAuthHeader() })
      ]);
      setScrapingJobs(scrapingRes.data);
      setAiJobs(aiRes.data);
      setPdfUploads(pdfRes.data);
      setScrapingSources(sourcesRes.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchJobs();
    const interval = setInterval(fetchJobs, 5000);
    return () => clearInterval(interval);
  }, [fetchJobs]);

  const startScraping = async (source = 'all') => {
    setScrapingLoading(true);
    try {
      await axios.post(`${API_URL}/scraping/start?source=${source}`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Scraping démarré');
      fetchJobs();
      setTimeout(onRefresh, 3000);
    } catch (error) {
      toast.error('Erreur lors du scraping');
    } finally {
      setScrapingLoading(false);
    }
  };

  const generateAI = async () => {
    if (!aiTopic.trim()) {
      toast.error('Veuillez entrer un sujet');
      return;
    }
    setAiLoading(true);
    try {
      await axios.post(`${API_URL}/ai/generate`, {
        topic: aiTopic,
        category: aiCategory
      }, { headers: getAuthHeader() });
      toast.success('Génération IA démarrée');
      setAiTopic('');
      fetchJobs();
      setTimeout(onRefresh, 5000);
    } catch (error) {
      toast.error('Erreur lors de la génération');
    } finally {
      setAiLoading(false);
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.pdf')) {
      toast.error('Seuls les fichiers PDF sont acceptés');
      return;
    }
    
    setPdfLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API_URL}/pdf/upload`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('PDF en cours de traitement');
      fetchJobs();
      setTimeout(onRefresh, 5000);
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setPdfLoading(false);
    }
  };

  const aiSuggestions = [
    { topic: "L'histoire de la construction du Château de Versailles", category: "histoire" },
    { topic: "Les jardins de Le Nôtre à Versailles", category: "histoire" },
    { topic: "Marie-Antoinette et le Petit Trianon", category: "histoire" },
    { topic: "La vie quotidienne à la cour de Louis XIV", category: "culture" },
    { topic: "Les fontaines et jeux d'eau de Versailles", category: "histoire" },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-playfair text-2xl font-bold text-stone-900">Génération de contenu</h2>
        <p className="font-inter text-stone-500 mt-1">Scraping automatique, génération IA et import PDF</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Scraping Section */}
        <div className="bg-white border border-stone-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Download className="h-6 w-6 text-blue-600" />
            <h3 className="font-playfair text-lg font-semibold">Scraping</h3>
          </div>
          
          <p className="font-inter text-sm text-stone-500 mb-4">
            Récupère les actualités depuis les sources officielles avec détection des doublons.
          </p>

          <div className="space-y-3">
            <Select value={selectedSource} onValueChange={setSelectedSource}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner une source" />
              </SelectTrigger>
              <SelectContent>
                {scrapingSources.map(source => (
                  <SelectItem key={source.id} value={source.id}>{source.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button 
              onClick={() => startScraping(selectedSource)}
              disabled={scrapingLoading}
              className="w-full bg-blue-600 hover:bg-blue-700"
              data-testid="start-scraping-btn"
            >
              {scrapingLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Lancer le scraping
            </Button>
          </div>

          {scrapingJobs.length > 0 && (
            <div className="mt-4 pt-4 border-t border-stone-200">
              <p className="font-inter text-xs font-semibold mb-2 text-stone-600">Historique</p>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {scrapingJobs.slice(0, 5).map((job) => (
                  <div key={job.id} className="flex items-center justify-between text-xs">
                    <span className="text-stone-500 truncate">{job.source}</span>
                    <span className={`px-1.5 py-0.5 ${
                      job.status === 'completed' ? 'bg-green-100 text-green-800' :
                      job.status === 'failed' ? 'bg-red-100 text-red-800' :
                      'bg-amber-100 text-amber-800'
                    }`}>
                      {job.status === 'completed' ? `${job.articles_count} art.` : job.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* AI Generation Section */}
        <div className="bg-white border border-stone-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Sparkles className="h-6 w-6 text-purple-600" />
            <h3 className="font-playfair text-lg font-semibold">Génération IA</h3>
          </div>
          
          <p className="font-inter text-sm text-stone-500 mb-4">
            Génère des articles sur Versailles avec Claude Sonnet 4.5.
          </p>

          <div className="space-y-3">
            <Textarea 
              value={aiTopic}
              onChange={(e) => setAiTopic(e.target.value)}
              placeholder="Ex: L'histoire de la Galerie des Glaces"
              rows={2}
              className="text-sm"
              data-testid="ai-topic-input"
            />
            
            <Select value={aiCategory} onValueChange={setAiCategory}>
              <SelectTrigger data-testid="ai-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button 
              onClick={generateAI}
              disabled={aiLoading}
              className="w-full bg-purple-600 hover:bg-purple-700"
              data-testid="generate-ai-btn"
            >
              {aiLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
              Générer
            </Button>
          </div>

          <div className="mt-4 pt-4 border-t border-stone-200">
            <p className="font-inter text-xs font-semibold mb-2 text-stone-600">Suggestions</p>
            <div className="space-y-1">
              {aiSuggestions.slice(0, 3).map((s, i) => (
                <button
                  key={i}
                  onClick={() => { setAiTopic(s.topic); setAiCategory(s.category); }}
                  className="block w-full text-left text-xs text-stone-500 hover:text-purple-600 truncate"
                >
                  → {s.topic}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* PDF Upload Section */}
        <div className="bg-white border border-stone-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <FileUp className="h-6 w-6 text-amber-600" />
            <h3 className="font-playfair text-lg font-semibold">Import PDF</h3>
          </div>
          
          <p className="font-inter text-sm text-stone-500 mb-4">
            Importez vos anciens journaux PDF pour en extraire les articles.
          </p>

          <div className="space-y-3">
            <label className="block">
              <input
                type="file"
                accept=".pdf"
                onChange={handlePdfUpload}
                className="hidden"
                data-testid="pdf-upload-input"
              />
              <Button 
                variant="outline"
                className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                disabled={pdfLoading}
                onClick={() => document.querySelector('[data-testid="pdf-upload-input"]').click()}
              >
                {pdfLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                Sélectionner un PDF
              </Button>
            </label>
          </div>

          {pdfUploads.length > 0 && (
            <div className="mt-4 pt-4 border-t border-stone-200">
              <p className="font-inter text-xs font-semibold mb-2 text-stone-600">Imports récents</p>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {pdfUploads.slice(0, 5).map((upload) => (
                  <div key={upload.id} className="flex items-center justify-between text-xs">
                    <span className="text-stone-500 truncate max-w-[120px]">{upload.filename}</span>
                    <span className={`px-1.5 py-0.5 ${
                      upload.status === 'completed' ? 'bg-green-100 text-green-800' :
                      upload.status === 'failed' ? 'bg-red-100 text-red-800' :
                      'bg-amber-100 text-amber-800'
                    }`}>
                      {upload.status === 'completed' ? `${upload.articles_extracted} art.` : upload.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// CRON Settings Component
const CronSettings = ({ getAuthHeader }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${API_URL}/cron/config`, { headers: getAuthHeader() });
        setConfig(res.data);
      } catch (error) {
        console.error('Error fetching cron config:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, [getAuthHeader]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/cron/config`, config, { headers: getAuthHeader() });
      toast.success('Configuration sauvegardée');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      await axios.post(`${API_URL}/cron/run`, {}, { headers: getAuthHeader() });
      toast.success('Génération quotidienne lancée');
    } catch (error) {
      toast.error('Erreur lors du lancement');
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-red-800" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-playfair text-2xl font-bold text-stone-900">Automatisation</h2>
        <p className="font-inter text-stone-500 mt-1">Configuration de la génération automatique quotidienne</p>
      </div>

      <div className="bg-white border border-stone-200 p-6 max-w-2xl">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-inter font-medium">Génération automatique</p>
              <p className="font-inter text-sm text-stone-500">Active la génération de 5 articles par jour</p>
            </div>
            <Switch
              checked={config?.enabled}
              onCheckedChange={(checked) => setConfig({...config, enabled: checked})}
              data-testid="cron-enabled-switch"
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-inter font-medium">Scraping automatique</p>
              <p className="font-inter text-sm text-stone-500">Inclut le scraping dans la génération quotidienne</p>
            </div>
            <Switch
              checked={config?.scraping_enabled}
              onCheckedChange={(checked) => setConfig({...config, scraping_enabled: checked})}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-inter font-medium">Génération IA automatique</p>
              <p className="font-inter text-sm text-stone-500">Inclut la génération IA dans la génération quotidienne</p>
            </div>
            <Switch
              checked={config?.ai_enabled}
              onCheckedChange={(checked) => setConfig({...config, ai_enabled: checked})}
            />
          </div>

          <div>
            <Label className="font-inter font-medium">Articles par jour</Label>
            <Input
              type="number"
              min="1"
              max="20"
              value={config?.articles_per_day || 5}
              onChange={(e) => setConfig({...config, articles_per_day: parseInt(e.target.value)})}
              className="mt-2 w-32"
            />
          </div>

          {config?.last_run && (
            <div className="pt-4 border-t border-stone-200">
              <p className="font-inter text-sm text-stone-500">
                <Clock className="h-4 w-4 inline mr-1" />
                Dernière exécution: {new Date(config.last_run).toLocaleString('fr-FR')}
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button onClick={saveConfig} disabled={saving} className="bg-red-800 hover:bg-red-900">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Settings className="h-4 w-4 mr-2" />}
              Sauvegarder
            </Button>
            <Button onClick={runNow} disabled={running} variant="outline">
              {running ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
              Exécuter maintenant
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Admin Dashboard
export default function AdminDashboard() {
  const { user, logout, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState({ total: 0, published: 0, by_category: {}, by_source: {} });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [articlesRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/articles?published_only=false&limit=100`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/articles/stats`, { headers: getAuthHeader() })
      ]);
      setArticles(articlesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin', label: 'Tableau de bord', icon: LayoutDashboard },
    { path: '/admin/articles', label: 'Articles', icon: FileText },
    { path: '/admin/generation', label: 'Génération', icon: Sparkles },
    { path: '/admin/cron', label: 'Automatisation', icon: Clock },
  ];

  return (
    <div className="min-h-screen bg-stone-100 flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-stone-200 flex flex-col">
        {/* Logo */}
        <div className="p-4 border-b border-stone-200">
          <Link to="/" className="block">
            <img 
              src="https://customer-assets.emergentagent.com/job_versailles-actu/artifacts/x061u1gg_logo%20Versailles%2B.jpeg" 
              alt="Versailles Plus" 
              className="h-12 object-contain"
            />
          </Link>
          <p className="font-inter text-xs text-stone-500 mt-1">Administration</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {navItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={`flex items-center gap-3 px-4 py-3 font-inter text-sm transition-colors duration-200 ${
                    location.pathname === item.path
                      ? 'bg-red-800 text-white'
                      : 'text-stone-700 hover:bg-stone-100'
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* User */}
        <div className="p-4 border-t border-stone-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-800 rounded-full flex items-center justify-center">
              <span className="font-inter text-white font-semibold">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <p className="font-inter text-sm font-medium text-stone-900">{user?.name}</p>
              <p className="font-inter text-xs text-stone-500">{user?.email}</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 p-8 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-red-800" />
          </div>
        ) : (
          <Routes>
            <Route path="/" element={<DashboardOverview stats={stats} />} />
            <Route path="/articles" element={
              <ArticlesManagement 
                articles={articles} 
                onRefresh={fetchData} 
                getAuthHeader={getAuthHeader}
              />
            } />
            <Route path="/generation" element={
              <ContentGeneration 
                getAuthHeader={getAuthHeader}
                onRefresh={fetchData}
              />
            } />
            <Route path="/cron" element={
              <CronSettings getAuthHeader={getAuthHeader} />
            } />
          </Routes>
        )}
      </main>
    </div>
  );
}
